// These components own only their controls. Upload-Lift owns its DOM and upload properties.
const normalize = (value) => String(value ?? '').trim().toLowerCase().replace(/\s+/g, '');

class PersonalizedMugSize extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.details = this.closest('.product-details');
    this.picker = this.details?.querySelector('variant-picker');
    if (!this.picker || this.picker.dataset.productId !== this.dataset.productId) return;

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-size-value]');
      if (!button || !this.contains(button)) return;
      const input = this.radios().find((radio) => normalize(radio.value) === normalize(button.dataset.sizeValue));
      // Native change handling remains responsible for variant IDs, availability, price and media.
      input?.click();
      this.sync();
    }, { signal });
    this.picker.addEventListener('change', () => this.sync(), { signal });
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this.picker, { childList: true, subtree: true, attributes: true });
    this.sync();
  }

  radios() {
    return Array.from(this.picker.querySelectorAll('input[type="radio"][data-option-name]'))
      .filter((input) => normalize(input.dataset.optionName) === normalize(this.dataset.optionName));
  }

  sync() {
    const radios = this.radios();
    const buttons = Array.from(this.querySelectorAll('[data-size-value]'));
    const group = radios[0]?.closest('.variant-option');
    const ready = buttons.length === 2 && radios.length === 2 &&
      ['11oz', '15oz'].every((value) => radios.some((radio) => normalize(radio.value) === value)) &&
      buttons.every((button) => radios.some((radio) => normalize(radio.value) === normalize(button.dataset.sizeValue)));

    this.hidden = !ready;
    if (this.group && this.group !== group) this.group.classList.remove('la-personalized-native-size');
    this.group = group;
    // Avoid mutation-observer loops and leave the native picker visible if enhancement is unsupported.
    if (group && group.classList.contains('la-personalized-native-size') !== ready) {
      group.classList.toggle('la-personalized-native-size', ready);
    }
    for (const button of buttons) {
      const radio = radios.find((input) => normalize(input.value) === normalize(button.dataset.sizeValue));
      button.setAttribute('aria-pressed', String(Boolean(radio?.checked)));
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.group?.classList.remove('la-personalized-native-size');
  }
}

class PersonalizedPetName extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.nameInput = this.querySelector('[data-pet-name]');
    this.noName = this.querySelector('[data-no-name]');
    this.noNameProperty = this.querySelector('[data-no-name-property]');
    this.error = this.querySelector('.la-personalized__error');
    if (!this.nameInput || !this.noName || !this.noNameProperty || !this.error) return;

    this.addEventListener('input', () => this.sync(), { signal });
    this.addEventListener('change', (event) => {
      if (this.hasAttribute('data-portrait-name-layout')) {
        if (event.target.matches('[data-name-layout]')) this.noName.checked = event.target.dataset.nameLayout === 'none';
        if (event.target === this.noName) {
          const choice = this.querySelector(`[data-name-layout="${this.noName.checked ? 'none' : 'top'}"]`);
          if (choice) choice.checked = true;
        }
      }
      this.sync();
    }, { signal });
    this.nameInput.addEventListener('invalid', () => this.showError(), { signal });

    // Match the exact associated form, including if Shopify replaces it after initialization.
    document.addEventListener('submit', (event) => {
      if (event.target.getAttribute('id') !== this.dataset.formId) return;
      this.sync();
      if (!this.valid()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError();
        this.nameInput.focus();
      }
    }, { capture: true, signal });

    // The theme builds FormData(form); explicit form attributes include these external inputs.
    document.addEventListener('formdata', (event) => {
      if (event.target.getAttribute('id') !== this.dataset.formId) return;
      if (this.hasAttribute('data-holiday-name-inactive')) return;
      event.formData.set(this.nameInput.name, this.noName.checked ? '' : this.nameInput.value.trim());
      event.formData.set(this.noNameProperty.name, this.noName.checked ? 'Yes' : '');
      const layout = this.querySelector('[data-name-layout]:checked');
      if (layout) event.formData.set(layout.name, layout.value);
    }, { capture: true, signal });

    document.addEventListener('reset', (event) => {
      if (event.target.getAttribute('id') !== this.dataset.formId) return;
      this.noName.checked = false;
      queueMicrotask(() => { if (this.isConnected) this.sync(); });
    }, { capture: true, signal });
    this.sync();
  }

  valid() {
    if (this.hasAttribute('data-holiday-name-inactive')) return true;
    if (this.hasAttribute('data-portrait-name-layout') && !this.noName.checked && this.querySelector('[data-name-layout="repeated"]:checked')) {
      const names = this.nameInput.value.split(',').map((name) => name.trim()).filter(Boolean);
      return names.length >= 1 && names.length <= 2;
    }
    return this.noName.checked || this.nameInput.value.trim().length > 0;
  }

  sync() {
    if (this.hasAttribute('data-holiday-name-inactive')) {
      this.nameInput.disabled = true;
      this.nameInput.required = false;
      this.nameInput.setCustomValidity('');
      this.noNameProperty.disabled = true;
      this.error.hidden = true;
      return;
    }
    this.noNameProperty.disabled = false;
    if (this.noName.checked && !this.hasAttribute('data-portrait-name-layout')) this.nameInput.value = '';
    this.nameInput.disabled = this.noName.checked;
    this.nameInput.required = !this.noName.checked;
    this.noNameProperty.value = this.noName.checked ? 'Yes' : '';
    const message = this.hasAttribute('data-portrait-name-layout') && this.querySelector('[data-name-layout="repeated"]:checked') ? this.dataset.patternError : this.dataset.error;
    this.nameInput.setCustomValidity(this.valid() ? '' : message);
    this.error.textContent = message;
    if (this.valid()) {
      this.error.hidden = true;
      this.nameInput.removeAttribute('aria-invalid');
    }
    if (this.hasAttribute('data-portrait-name-layout')) {
      this.dispatchEvent(new Event('portrait:preview-change', { bubbles: true }));
    }
  }

  showError() {
    this.error.hidden = false;
    this.nameInput.setAttribute('aria-invalid', 'true');
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

if (!customElements.get('la-mug-size')) customElements.define('la-mug-size', PersonalizedMugSize);
if (!customElements.get('la-pet-name')) customElements.define('la-pet-name', PersonalizedPetName);
